'use client'

import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import styles from './LazyLoader.module.scss'

const PlaceholderSection = () => <div className={styles.placeholder} />

const LazyLoader = ({
  style,
  children,
  id,
  height,
  mobileHeight,
  hasMinHeight = true
}: {
  children: React.ReactNode
  style?: React.CSSProperties
  id?: string
  height?: number
  // Height to reserve below the mobile breakpoint, for sections that reflow to
  // a taller layout there (e.g. a 3-column grid stacking into one column).
  // Falls back to `height` when omitted.
  mobileHeight?: number
  hasMinHeight?: boolean
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      {
        rootMargin: '100px'
      }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [])

  // Passed as custom properties rather than an inline height so the mobile
  // value can be picked up by a media query in the stylesheet.
  const reservedHeight = {
    '--lazy-loader-height': height !== undefined ? `${height}px` : undefined,
    '--lazy-loader-height-mobile':
      mobileHeight !== undefined ? `${mobileHeight}px` : undefined
  } as React.CSSProperties

  return (
    <section
      className={clsx({ [styles.hasMinHeight]: hasMinHeight })}
      style={{ ...style, ...reservedHeight }}
      ref={ref}
      id={id}
    >
      {isVisible ? children : <PlaceholderSection />}
    </section>
  )
}

export default LazyLoader
